import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Moon, Dumbbell, Apple, Calendar, Trophy,
  TrendingUp, Flame, Clock, ChevronRight, GraduationCap,
  AlertTriangle, Activity, Target, Loader2, Heart, MapPin,
  Users, Plus, Edit2, CalendarDays
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart } from 'recharts';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';

const AthleteDetailPage = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [recoveryLogs, setRecoveryLogs] = useState([]);
  const [nutritionLogs, setNutritionLogs] = useState([]);
  const [badges, setBadges] = useState([]);
  const [academics, setAcademics] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAthleteData();
  }, [athleteId]);

  const fetchAthleteData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch athlete profile and stats
      const [athleteRes, workoutsRes, recoveryRes, nutritionRes, badgesRes, academicsRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/supporter/athlete/${athleteId}`, { headers }),
        axios.get(`${API}/supporter/athlete/${athleteId}/workouts`, { headers }).catch(() => ({ data: { workouts: [] } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/recovery`, { headers }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/nutrition`, { headers }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/badges`, { headers }).catch(() => ({ data: { badges: [], total_points: 0 } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/academics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/supporter/athlete/${athleteId}/schedule`, { headers }).catch(() => ({ data: { events: [] } })),
      ]);

      setAthlete(athleteRes.data);
      setWorkouts(workoutsRes.data.workouts || []);
      setRecoveryLogs(recoveryRes.data.logs || []);
      setNutritionLogs(nutritionRes.data.logs || []);
      setBadges(badgesRes.data);
      setAcademics(academicsRes.data);
      
      // Filter for upcoming events only
      const upcomingEvents = (scheduleRes.data.events || [])
        .filter(e => new Date(e.start_time) >= new Date())
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setSchedule(upcomingEvents);
    } catch (error) {
      console.error('Failed to fetch athlete data:', error);
      toast.error('Failed to load athlete data');
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      practice: 'bg-aura-primary',
      game: 'bg-green-500',
      personal_training: 'bg-purple-500',
      conditioning: 'bg-orange-500',
      workout: 'bg-aura-secondary',
      school: 'bg-blue-500',
      family: 'bg-pink-500',
      recovery: 'bg-cyan-500',
      appointment: 'bg-amber-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getRecoveryColor = (score) => {
    if (!score) return 'text-aura-text-muted';
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-cyan-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getRecoveryBg = (score) => {
    if (!score) return 'bg-white/10';
    if (score >= 80) return 'bg-green-500/20';
    if (score >= 60) return 'bg-cyan-500/20';
    if (score >= 40) return 'bg-yellow-500/20';
    return 'bg-red-500/20';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aura-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-aura-primary" />
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="min-h-screen bg-aura-bg flex flex-col items-center justify-center">
        <p className="text-white mb-4">Athlete not found or access denied</p>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const latestRecovery = recoveryLogs[0];
  const recoveryChartData = recoveryLogs.slice(0, 7).reverse().map((log, idx) => ({
    day: `Day ${idx + 1}`,
    score: log.recovery_score,
    sleep: log.sleep_hours
  }));

  const earnedBadges = badges.badges?.filter(b => b.earned) || [];
  const totalAura = badges.total_points || 0;

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="p-2 text-aura-text-muted hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-aura-secondary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">AURA</span>
          </div>
          <Badge className="bg-aura-secondary/20 text-aura-secondary border-0">
            Supporter View
          </Badge>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Athlete Profile Header */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-aura-primary flex items-center justify-center flex-shrink-0">
            {athlete.picture ? (
              <img src={athlete.picture} alt="" className="w-full h-full rounded-full object-cover" />
            ) : (
              <span className="font-heading font-bold text-white text-2xl">
                {athlete.name?.charAt(0)?.toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              {athlete.name}
            </h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className="bg-white/10 text-white border-0">
                {athlete.sport || 'General Athlete'}
              </Badge>
              {athlete.org_name && (
                <Badge className="bg-aura-secondary/20 text-aura-secondary border-0">
                  {athlete.org_name}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Key Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className={`${getRecoveryBg(latestRecovery?.recovery_score)} border-0`}>
            <CardContent className="p-4 text-center">
              <Moon className={`w-6 h-6 ${getRecoveryColor(latestRecovery?.recovery_score)} mx-auto mb-2`} />
              <p className={`font-heading font-bold text-3xl ${getRecoveryColor(latestRecovery?.recovery_score)}`}>
                {latestRecovery?.recovery_score || '--'}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Recovery</p>
            </CardContent>
          </Card>
          
          <Card className="bg-aura-primary/20 border-0">
            <CardContent className="p-4 text-center">
              <Dumbbell className="w-6 h-6 text-aura-primary mx-auto mb-2" />
              <p className="font-heading font-bold text-3xl text-aura-primary">
                {athlete.workouts_this_week || workouts.length}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Workouts/Week</p>
            </CardContent>
          </Card>
          
          <Card className="bg-yellow-500/20 border-0">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-3xl text-yellow-400">
                {totalAura}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Aura Score</p>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-500/20 border-0">
            <CardContent className="p-4 text-center">
              <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" />
              <p className="font-heading font-bold text-3xl text-orange-400">
                {athlete.streak || 0}
              </p>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Day Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Section */}
        {latestRecovery?.recovery_score < 60 && (
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-white">Low Recovery Alert</p>
                  <p className="text-sm text-aura-text-muted mt-1">
                    {athlete.name}'s recovery score is {latestRecovery?.recovery_score}. They may need extra rest or lighter training today.
                    {latestRecovery?.sleep_hours < 7 && ` Only got ${latestRecovery.sleep_hours} hours of sleep.`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Schedule - What Athlete is Responsible For */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-aura-primary" />
                Upcoming Responsibilities
              </CardTitle>
              <Link to="/family/schedule">
                <Button size="sm" className="bg-aura-primary hover:bg-aura-primary-hover text-white">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Event
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {schedule.length > 0 ? (
              <div className="space-y-3">
                {schedule.slice(0, 5).map((event) => {
                  const eventDate = parseISO(event.start_time);
                  return (
                    <div key={event.event_id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                      <div className={`w-2 h-full min-h-[40px] rounded-full ${getEventTypeColor(event.event_type)}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-white">{event.title}</p>
                          <Badge variant="outline" className="text-xs capitalize">
                            {event.event_type?.replace('_', ' ')}
                          </Badge>
                          {event.requires_parent && (
                            <Badge className="bg-aura-secondary/20 text-aura-secondary border-0 text-xs">
                              <Users className="w-3 h-3 mr-1" />
                              Parent Needed
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-aura-text-muted">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {isToday(eventDate) ? 'Today' : isTomorrow(eventDate) ? 'Tomorrow' : format(eventDate, 'EEE, MMM d')} at {format(eventDate, 'h:mm a')}
                          </span>
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {schedule.length > 5 && (
                  <Link to="/family/schedule">
                    <p className="text-sm text-aura-secondary text-center py-2">
                      +{schedule.length - 5} more events
                    </p>
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <Calendar className="w-10 h-10 text-aura-text-subtle mx-auto mb-2" />
                <p className="text-aura-text-muted mb-3">No upcoming events scheduled</p>
                <Link to="/family/schedule">
                  <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white">
                    Schedule Event
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recovery Trend Chart */}
        {recoveryChartData.length > 0 && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Activity className="w-5 h-5 text-aura-secondary" />
                Recovery Trend (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={recoveryChartData}>
                    <defs>
                      <linearGradient id="recoveryGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" stroke="#71717A" fontSize={12} />
                    <YAxis stroke="#71717A" fontSize={12} domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#06B6D4" 
                      strokeWidth={2}
                      fill="url(#recoveryGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Workouts */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-aura-primary" />
              Recent Workouts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {workouts.length > 0 ? (
              <div className="space-y-3">
                {workouts.slice(0, 5).map((workout, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-aura-primary/20 rounded-lg flex items-center justify-center">
                        <Dumbbell className="w-5 h-5 text-aura-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{workout.name}</p>
                        <p className="text-xs text-aura-text-muted">
                          {workout.duration_minutes} min • {workout.intensity} intensity
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-aura-text-muted">
                        {new Date(workout.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-aura-text-muted text-center py-6">No recent workouts logged</p>
            )}
          </CardContent>
        </Card>

        {/* Latest Recovery Details */}
        {latestRecovery && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Moon className="w-5 h-5 text-aura-secondary" />
                Today's Recovery Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Sleep</p>
                  <p className="font-heading font-bold text-xl text-white">{latestRecovery.sleep_hours}h</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Sleep Quality</p>
                  <p className="font-heading font-bold text-xl text-white">{latestRecovery.sleep_quality}/5</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Stress</p>
                  <p className="font-heading font-bold text-xl text-white">{latestRecovery.stress_level}/5</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-aura-text-muted uppercase">Fatigue</p>
                  <p className="font-heading font-bold text-xl text-white">{latestRecovery.fatigue_level}/5</p>
                </div>
              </div>
              {latestRecovery.soreness_areas?.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-aura-text-muted uppercase mb-2">Sore Areas</p>
                  <div className="flex flex-wrap gap-2">
                    {latestRecovery.soreness_areas.map((area, idx) => (
                      <Badge key={idx} className="bg-red-500/20 text-red-400 border-0">
                        {area}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {latestRecovery.notes && (
                <div className="mt-4 p-3 bg-white/5 rounded-lg">
                  <p className="text-xs text-aura-text-muted uppercase mb-1">Notes</p>
                  <p className="text-white text-sm">{latestRecovery.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Nutrition Summary */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Apple className="w-5 h-5 text-green-400" />
              Recent Nutrition
            </CardTitle>
          </CardHeader>
          <CardContent>
            {nutritionLogs.length > 0 ? (
              <div className="space-y-3">
                {nutritionLogs.slice(0, 3).map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <Apple className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white capitalize">{log.meal_type}</p>
                        <p className="text-xs text-aura-text-muted">
                          {log.calories} cal • {log.protein}g protein
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-aura-text-muted">
                        {new Date(log.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-aura-text-muted text-center py-6">No recent meals logged</p>
            )}
          </CardContent>
        </Card>

        {/* Academics (if available) */}
        {academics && academics.courses && academics.courses.length > 0 && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-blue-400" />
                Academic Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 mb-4">
                <div>
                  <p className="text-xs text-aura-text-muted uppercase">Current GPA</p>
                  <p className="font-heading font-bold text-2xl text-white">
                    {academics.gpa?.toFixed(2) || '--'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-aura-text-muted uppercase">Active Courses</p>
                  <p className="font-heading font-bold text-2xl text-white">
                    {academics.courses?.length || 0}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {academics.courses.slice(0, 4).map((course, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                    <span className="text-white text-sm">{course.name}</span>
                    <span className="text-aura-primary font-medium">{course.grade || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Badges/Achievements */}
        {earnedBadges.length > 0 && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Recent Achievements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                {earnedBadges.slice(0, 6).map((badge, idx) => {
                  const tierColors = {
                    bronze: 'bg-amber-600',
                    silver: 'bg-slate-400',
                    gold: 'bg-yellow-400'
                  };
                  return (
                    <div key={idx} className="text-center">
                      <div className={`w-12 h-12 mx-auto rounded-full ${tierColors[badge.tier] || 'bg-aura-primary'} flex items-center justify-center mb-2`}>
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-xs text-white font-medium truncate">{badge.name}</p>
                      <p className="text-xs text-aura-text-muted capitalize">{badge.tier}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Support Message */}
        <div className="aura-card p-6 text-center bg-gradient-to-br from-aura-secondary/5 to-transparent">
          <Heart className="w-8 h-8 text-aura-secondary mx-auto mb-3" />
          <p className="font-heading font-bold text-xl text-white uppercase tracking-tight">
            Keep Supporting!
          </p>
          <p className="text-aura-text-muted mt-2">
            Your visibility helps {athlete.name} stay accountable while maintaining their independence.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AthleteDetailPage;
